import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { NAV_BY_ROLE, STAFF_ANALYTICS_LINK } from '../../constants/navLinks';
import BrandLogo from '../brand/BrandLogo';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import ThemeToggle from '../ui/ThemeToggle';

const linkClass = ({ isActive }) =>
  `block truncate rounded-lg px-3 py-2 text-sm transition ${
    isActive
      ? 'bg-glow-600/20 text-glow-300'
      : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100'
  }`;

const Sidebar = ({ open, onClose, onNavigate }) => {
  const { t } = useTranslation();
  const { role, user, logout, isImpersonating } = useAuth();
  let links = NAV_BY_ROLE[role] || [];
  if (role === 'SALON_STAFF') {
    const canReviews =
      user?.canManageAppointments ||
      user?.permissions?.canManageAppointments ||
      user?.canManageOrders ||
      user?.permissions?.canManageOrders;
    const canUsePos =
      user?.canUsePos !== undefined
        ? !!user.canUsePos
        : user?.permissions?.canUsePos !== undefined
          ? !!user.permissions.canUsePos
          : true;
    links = links.filter((link) => {
      if (link.to === '/salon/reviews') return canReviews;
      if (link.to === '/salon/pos') return canUsePos;
      return true;
    });
    if (user?.canViewAnalytics || user?.permissions?.canViewAnalytics) {
      links = [...links, STAFF_ANALYTICS_LINK];
    }
  }

  const handleNav = () => onNavigate?.();

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex h-screen w-[min(18rem,90vw)] flex-col border-r border-zinc-200 bg-white shadow-xl transition-transform duration-200 ease-out dark:border-aura-800 dark:bg-[#0e0818] ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
      aria-hidden={!open}
    >
      <div className="border-b border-zinc-200 p-4 dark:border-aura-800">
        <div className="flex items-start justify-between gap-2">
          <BrandLogo to="/" size="sm" showWordmark onClick={handleNav} />
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-aura-900 dark:hover:text-zinc-200 md:hidden"
            aria-label={t('common.closeMenu')}
          >
            ✕
          </button>
        </div>
        {isImpersonating && (
          <p className="mt-2 text-xs text-amber-400">{t('nav.impersonationMode')}</p>
        )}
        <p className="mt-2 truncate text-xs text-zinc-600 dark:text-zinc-500">
          {user?.firstName} {user?.lastName}
        </p>
        <p className="text-xs text-gold-500/90">
          {role ? t(`roles.${role}`, { defaultValue: role.replace(/_/g, ' ') }) : ''}
        </p>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-4">
        {links.map((link) =>
          link.newTab ? (
            <a
              key={link.to}
              href={link.to}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNav}
              className="block truncate rounded-lg px-3 py-2 text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            >
              {t(link.labelKey)}
            </a>
          ) : (
            <NavLink key={link.to} to={link.to} end className={linkClass} onClick={handleNav}>
              {t(link.labelKey)}
            </NavLink>
          )
        )}
        <NavLink to="/notifications" className={linkClass} onClick={handleNav}>
          {t('common.notifications')}
        </NavLink>
        <NavLink to="/settings/profile" className={linkClass} onClick={handleNav}>
          {t('nav.profileSettings')}
        </NavLink>
      </nav>

      <div className="space-y-3 border-t border-zinc-200 p-4 dark:border-aura-800">
        <LanguageSwitcher className="w-full" selectClassName="w-full" />
        <ThemeToggle className="w-full justify-center" />
        <button
          type="button"
          onClick={logout}
          className="w-full rounded-lg px-3 py-2 text-left text-sm text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        >
          {t('common.signOut')}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
